/**
 * Lithography: dose and focus in, critical dimension out.
 *
 *   1. OPTICAL   optics.ts builds the aerial image from diffraction orders
 *   2. CHEMICAL  exposure generates acid; the post-exposure bake blurs it
 *   3. DEVELOP   resist clears where acid crosses a threshold -> a CD
 *   4. STOCHASTIC finite photon counts make that edge wander
 *
 * The chain matters more here than in any other module, because the two numbers
 * a technician actually sets - dose and focus - reach the wafer through every
 * link of it. Dose scales the acid. Focus changes the image the acid is made
 * from. Neither one touches CD directly, and yet CD is what gets measured and
 * what the lot is judged on.
 *
 * Nothing below computes a CD from a formula in dose and focus. The CD is found
 * by looking at where the developed edge lands, which is why the Bossung curves
 * bend the way real ones do.
 */

import { aerialImage, DUV_IMMERSION, photonsPerNm2, type Illumination } from './optics'
import { clamp, mean, stdev } from '../../core/math'

export interface LithoRecipe {
  /** Exposure dose [mJ/cm^2]. */
  doseMjCm2: number
  /** Focus offset from the best focal plane [nm]. Negative is into the wafer. */
  focusNm: number
  /** Grating pitch being printed [nm]. Half-pitch is the target CD. */
  pitchNm: number
  /** Post-exposure bake diffusion length [nm] - set by bake time and temperature. */
  pebBlurNm: number
  /** Resist contrast (Dill-like); higher clears more abruptly at the threshold. */
  resistContrast: number
  /** Development threshold as a fraction of maximum acid. */
  developThreshold: number
}

export const DEFAULT_LITHO_RECIPE: LithoRecipe = {
  doseMjCm2: 53.5,
  focusNm: 0,
  pitchNm: 180,
  pebBlurNm: 6,
  resistContrast: 6,
  developThreshold: 0.35,
}

export const LITHO_LIMITS: Record<
  keyof LithoRecipe,
  { min: number; max: number; step: number; unit: string; label: string }
> = {
  doseMjCm2: { min: 8, max: 90, step: 0.5, unit: 'mJ/cm²', label: 'Exposure dose' },
  focusNm: { min: -160, max: 160, step: 5, unit: 'nm', label: 'Focus offset' },
  pitchNm: { min: 40, max: 400, step: 2, unit: 'nm', label: 'Pitch' },
  pebBlurNm: { min: 1, max: 20, step: 0.5, unit: 'nm', label: 'PEB diffusion length' },
  resistContrast: { min: 2, max: 14, step: 0.5, unit: '', label: 'Resist contrast' },
  developThreshold: { min: 0.15, max: 0.6, step: 0.01, unit: '', label: 'Develop threshold' },
}

export interface LithoResult {
  /** Printed critical dimension [nm]. */
  cdNm: number
  /** Target CD - half the pitch. */
  targetCdNm: number
  /** CD error against target [%]. */
  cdErrorPct: number
  /** Image contrast. */
  contrast: number
  /** Normalised image log slope. */
  nils: number
  /** k1 factor. */
  k1: number
  /** Line edge roughness, 3 sigma [nm], from photon statistics. */
  lerNm: number
  /** Photons per square nanometre delivered at this dose. */
  photonsPerNm2: number
  /** True when the feature did not print at all. */
  failedToPrint: boolean
  /** Sampled acid profile after bake, for display. */
  acid: Float64Array
  /** Aerial image intensity, for display. */
  aerial: Float64Array
  /** Sample positions [nm]. */
  x: Float64Array
}

/**
 * Convolve a periodic profile with a Gaussian, in place of a real diffusion
 * solve. The post-exposure bake lets the photo-generated acid diffuse, and over
 * a bake that is short compared with the feature size that is exactly a
 * Gaussian blur - which is why lithographers quote a single "diffusion length"
 * rather than a bake time and a temperature.
 */
function blurPeriodic(src: Float64Array, sigmaSamples: number): Float64Array {
  const n = src.length
  const out = new Float64Array(n)
  if (sigmaSamples < 0.05) {
    out.set(src)
    return out
  }
  const radius = Math.max(1, Math.ceil(sigmaSamples * 3))
  const kernel = new Float64Array(2 * radius + 1)
  let norm = 0
  for (let i = -radius; i <= radius; i++) {
    const w = Math.exp(-(i * i) / (2 * sigmaSamples * sigmaSamples))
    kernel[i + radius] = w
    norm += w
  }
  for (let i = 0; i < n; i++) {
    let acc = 0
    for (let j = -radius; j <= radius; j++) {
      // Periodic: the grating repeats, so the edges wrap.
      const idx = ((i + j) % n + n) % n
      acc += src[idx] * kernel[j + radius]
    }
    out[i] = acc / norm
  }
  return out
}

/**
 * Expose, bake, develop, and measure.
 *
 * The CD is found by walking the developed profile and measuring the width that
 * survives - not by evaluating a CD equation. That is what lets the same code
 * produce a line that is too thin at high dose, too thick at low dose, and
 * bridged or collapsed at the extremes.
 */
export function expose(recipe: LithoRecipe, ill: Illumination = DUV_IMMERSION): LithoResult {
  const pitchM = recipe.pitchNm * 1e-9
  const samples = 256
  const img = aerialImage(pitchM, recipe.focusNm * 1e-9, ill, samples)

  // --- exposure: acid generated where light lands ---
  //
  // Dill-style saturating generation. The rate constant is anchored so that at
  // the REFERENCE dose, acid reaches the development threshold exactly where
  // the aerial image is at its mean. That anchoring is what makes the nominal
  // recipe print its target CD instead of an arbitrary one, and it is the
  // lithographic equivalent of calibrating a tool to size at nominal dose -
  // which is the first thing anyone does with a new resist.
  const REFERENCE_DOSE = 30
  const threshold = recipe.developThreshold
  const alpha = -Math.log(1 - threshold) / REFERENCE_DOSE

  const acidRaw = new Float64Array(samples)
  for (let i = 0; i < samples; i++) {
    acidRaw[i] = 1 - Math.exp(-alpha * recipe.doseMjCm2 * img.intensity[i])
  }

  // --- post-exposure bake: acid diffuses ---
  const dxNm = recipe.pitchNm / samples
  const acid = blurPeriodic(acidRaw, recipe.pebBlurNm / dxNm)

  // --- development ---
  //
  // Not a hard threshold. A real resist dissolves at a rate that depends
  // steeply but continuously on acid concentration, and the steepness IS the
  // resist contrast gamma. Integrating the surviving fraction rather than
  // counting pixels above a cutoff is what lets contrast matter at all: a
  // low-contrast resist gives a sloped foot and a CD that drifts with dose far
  // faster than a high-contrast one.
  const gamma = recipe.resistContrast
  let surviving = 0
  for (let i = 0; i < samples; i++) {
    const ratio = acid[i] / Math.max(threshold, 1e-9)
    surviving += 1 / (1 + Math.pow(ratio, gamma))
  }
  const cdNm = surviving * dxNm

  const targetCdNm = recipe.pitchNm / 2
  const failedToPrint = img.unresolved || surviving === 0 || surviving === samples

  // --- stochastics: a finite number of photons defines that edge ---
  //
  // The edge position is set by where acid crosses a threshold, and acid is
  // made by counting photons. In the blurred volume that decides one edge,
  // the photon count fluctuates as sqrt(N), so the edge does too - and a
  // steeper image (higher NILS) converts a given count fluctuation into less
  // edge movement, which is why NILS predicts roughness.
  const photons = photonsPerNm2(recipe.doseMjCm2, ill.wavelengthM)
  const blur = Math.max(recipe.pebBlurNm, 0.5)

  // Roughness has two independent sources and they add in quadrature.
  //
  // MATERIAL: the resist is not a continuum. Polymer chains, photoacid
  // generators and quencher are discrete and unevenly distributed, and that
  // sets a floor no amount of dose can buy back. It scales as 1/NILS because a
  // steeper image converts a given chemical fluctuation into less edge motion.
  //
  // SHOT NOISE: the photons themselves are counted. This term is what dose
  // buys back, and it is the one that dominates at EUV.
  //
  // The two prefactors are calibrated so a healthy 193 nm process lands near
  // 2.5 nm and a 36 nm-pitch EUV process near 5 nm, both of which are
  // representative measured values. The SCALINGS are physical; the absolute
  // level is fitted, exactly as with the etch rate constant.
  // Both terms fall as the bake diffusion length grows, because diffusion is a
  // low-pass filter: a longer diffusion length averages over more polymer and
  // over more photons. That is the trade a resist engineer actually makes -
  // blur buys smoothness and spends resolution, and both halves of that have to
  // be present or the knob teaches the wrong lesson.
  const REFERENCE_BLUR = 6
  const MATERIAL_COEFF = 9.5
  const SHOT_COEFF = 188
  // The material term scales more weakly than 1/NILS. A steeper image helps,
  // but chemical granularity is not purely an imaging problem, and a pure
  // 1/NILS law over-punishes the low-NILS features where EUV actually lives.
  const NILS_EXPONENT = 0.7
  const nils = img.nils
  const lerNm =
    nils > 0.05
      ? clamp(
          Math.hypot(
            MATERIAL_COEFF / (Math.pow(nils, NILS_EXPONENT) * Math.sqrt(blur / REFERENCE_BLUR)),
            SHOT_COEFF / (nils * blur * Math.sqrt(Math.max(photons, 1e-9))),
          ),
          0,
          60,
        )
      : 60

  return {
    cdNm: failedToPrint ? 0 : cdNm,
    targetCdNm,
    cdErrorPct: targetCdNm > 0 ? (100 * (cdNm - targetCdNm)) / targetCdNm : 0,
    contrast: img.contrast,
    nils: img.nils,
    k1: img.k1,
    lerNm,
    photonsPerNm2: photons,
    failedToPrint,
    acid,
    aerial: img.intensity,
    x: Float64Array.from(img.x, (v) => v * 1e9),
  }
}

export interface ProcessWindow {
  /** Focus values sampled [nm]. */
  focus: number[]
  /** Dose values sampled [mJ/cm^2]. */
  dose: number[]
  /** cd[doseIndex][focusIndex] in nm; NaN where nothing printed. */
  cd: number[][]
  /** Fraction of the sampled grid that lands inside the CD tolerance. */
  windowFraction: number
  /** Widest focus range, at any single dose, that stays in spec [nm]. */
  depthOfFocusNm: number
  /** Widest dose range, at best focus, that stays in spec [%]. */
  doseLatitudePct: number
}

/**
 * Map the focus-exposure window.
 *
 * This is the single most important measurement in lithography, and it is the
 * reason a scanner is specified by depth of focus rather than by resolution
 * alone: a feature that prints perfectly at one focus and nowhere else is
 * useless in production, because the wafer is never perfectly flat.
 */
export function processWindow(
  recipe: LithoRecipe,
  ill: Illumination = DUV_IMMERSION,
  tolerancePct = 10,
): ProcessWindow {
  const focus: number[] = []
  for (let f = -140; f <= 140; f += 10) focus.push(f)
  const dose: number[] = []
  for (let d = recipe.doseMjCm2 * 0.6; d <= recipe.doseMjCm2 * 1.6; d += recipe.doseMjCm2 * 0.05) {
    dose.push(d)
  }

  const target = recipe.pitchNm / 2
  const lo = target * (1 - tolerancePct / 100)
  const hi = target * (1 + tolerancePct / 100)

  const cd: number[][] = []
  let inSpec = 0
  let total = 0
  let bestDof = 0
  for (const d of dose) {
    const row: number[] = []
    let run = 0
    let bestRun = 0
    for (const f of focus) {
      const r = expose({ ...recipe, doseMjCm2: d, focusNm: f }, ill)
      const value = r.failedToPrint ? NaN : r.cdNm
      row.push(value)
      total++
      if (!Number.isNaN(value) && value >= lo && value <= hi) {
        inSpec++
        run += 10
        if (run > bestRun) bestRun = run
      } else {
        run = 0
      }
    }
    cd.push(row)
    if (bestRun > bestDof) bestDof = bestRun
  }

  // Dose latitude at best focus - the focus column nearest zero.
  const zeroIdx = focus.reduce((a, b, i) => (Math.abs(b) < Math.abs(focus[a]) ? i : a), 0)
  let doseLo = Infinity
  let doseHi = -Infinity
  for (let i = 0; i < dose.length; i++) {
    const v = cd[i][zeroIdx]
    if (!Number.isNaN(v) && v >= lo && v <= hi) {
      doseLo = Math.min(doseLo, dose[i])
      doseHi = Math.max(doseHi, dose[i])
    }
  }
  const doseLatitudePct =
    Number.isFinite(doseLo) && doseHi > doseLo
      ? (100 * (doseHi - doseLo)) / ((doseHi + doseLo) / 2)
      : 0

  return {
    focus,
    dose,
    cd,
    windowFraction: total > 0 ? inSpec / total : 0,
    depthOfFocusNm: bestDof,
    doseLatitudePct,
  }
}

/**
 * Repeat the same exposure with photon shot noise and report how much the CD
 * moves. Two nominally identical prints do not give the same edge, and at small
 * features that spread is the yield limit rather than any systematic error.
 */
export function stochasticSpread(
  recipe: LithoRecipe,
  ill: Illumination,
  trials = 40,
  rng: () => number = Math.random,
): { cds: number[]; meanCd: number; sigmaCd: number; failures: number } {
  const base = expose(recipe, ill)
  const cds: number[] = []
  let failures = 0

  for (let t = 0; t < trials; t++) {
    // Box-Muller normal deviate scaled by the LER the photon count implies.
    const u1 = Math.max(rng(), 1e-12)
    const u2 = rng()
    const g = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
    // Two independent edges, so the CD variance is twice one edge's.
    const jitter = (g * base.lerNm) / 3 * Math.SQRT2
    const cd = base.cdNm + jitter
    if (cd <= 0 || cd >= recipe.pitchNm) failures++
    cds.push(Math.max(cd, 0))
  }

  return { cds, meanCd: mean(cds), sigmaCd: stdev(cds), failures }
}

/**
 * Find the dose that prints the target CD - "dose to size".
 *
 * This is the first thing done with a new resist or a new feature, and it is
 * why a litho recipe's nominal dose is a measured quantity rather than a
 * chosen one. The aerial image of a real grating is not a sinusoid, so the
 * dose that puts the developed edge exactly on target has to be found, not
 * derived.
 *
 * Bisection: CD falls monotonically with dose over any sane range.
 */
export function doseToSize(
  recipe: LithoRecipe,
  ill: Illumination = DUV_IMMERSION,
  targetNm = recipe.pitchNm / 2,
): number {
  let lo = 4
  let hi = 400
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    const cd = expose({ ...recipe, doseMjCm2: mid }, ill).cdNm
    if (cd > targetNm) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}
